import { Input } from '@components/atoms/Input';
import { Button } from '@components/atoms/Button';
import { FormGroup } from '@components/molecules/form/FormGroup';

import { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Card } from '@components/atoms/Card';
import { FormikProps } from 'formik';
import Alert from '@components/atoms/Alert';
import { useTranslation } from '@hooks/useTranslation';

export interface LoginFormProps {
  formik: FormikProps<{
    email: string;
    password: string;
    rememberMe: boolean;
  }>;
  onForgotPassword: () => void;
}

// Module-scoped so it survives unmount/remount. LoginPage swaps this form out
// for a <Loading/> spinner while the login request is in flight, so a
// component-level ref is wiped every time a deeplink login fails — which
// re-armed auto-submit and looped forever on an already-used link.
let lastAutoSubmittedDeeplink: string | null = null;

const LINK_FOCUS_CLASSES =
  'rounded py-1 px-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-gray-800';

const LoginForm: React.FC<LoginFormProps> = ({ formik, onForgotPassword }) => {
  const tLogin = useTranslation('login');
  const location = useLocation();
  // `formik` is a new object every render, but these two methods are not: Formik
  // wraps them in its own useEventCallback, so their identity is stable and they
  // can be named as dependencies below.
  const { setFieldValue, handleSubmit } = formik;
  const emailError = formik.touched.email ? formik.errors.email : undefined;
  const passwordError = formik.touched.password
    ? formik.errors.password
    : undefined;
  const [showAlertForceChangePassword, setShowAlertForceChangePassword] =
    useState(false);
  const [shouldAutoSubmit, setShouldAutoSubmit] = useState(false);
  // Guard so we auto-submit the deeplink credentials at most once. Without this,
  // a failed login re-renders the form (and `formik` is a new object each
  // render), which would re-arm and re-submit forever.
  const hasAutoSubmitted = useRef(false);

  useEffect(() => {
    // Parse URL parameters manually to handle unencoded special characters in code
    const params: { [key: string]: string } = {};

    // A `#` in the code is the URL fragment delimiter, so the browser strips
    // everything from the `#` onward into location.hash before we can read it.
    // Stitch search + hash back together first so codes from already-sent
    // emails still work, whether the `#` lands mid-code ("Db#7vrba" ->
    // "code=Db" + "#7vrba") or at the very start ("#r6EGdGM" -> "code=" +
    // "#r6EGdGM"). Newly generated codes are URL-safe and won't have a hash.
    const search =
      location.search.substring(1) + location.hash; // hash includes its '#'

    // The email link is always "user=<email>&code=<code>", so:
    //  - user = everything between "user=" and the next "&"
    //  - code = everything after "code=" to the end of the string. It is the
    //    last parameter and may itself contain special characters (e.g. "+",
    //    "=", "#"), so we deliberately grab the whole remainder rather than
    //    stopping at the next "&".
    const userMatch = search.match(/user=([^&]*)/);
    const codeMatch = search.match(/code=(.*)$/);

    if (userMatch) {
      params.user = decodeURIComponent(userMatch[1]);
    }
    if (codeMatch) {
      params.code = codeMatch[1]; // Don't decode - use raw value
    }

    // Lowercase so invite links generated before email normalization (which may
    // carry a mixed-case address) still resolve to the canonical account
    const user = (params.user || '').replace(/ /g, '+').trim().toLowerCase();
    const code = params.code || '';

    if (user) {
      setShowAlertForceChangePassword(true);
      setFieldValue('email', user);
    }
    if (code) {
      setFieldValue('password', code);
    }

    // Auto-submit when both user and code are provided via deeplink — but only
    // once per set of credentials, tracked module-side so that a remount can't
    // re-arm it. A reused link (password already changed) fails, LoginPage
    // remounts us, and without this we would submit the same dead code forever.
    const deeplink = `${user}|${code}`;
    if (
      user &&
      code &&
      !hasAutoSubmitted.current &&
      lastAutoSubmittedDeeplink !== deeplink
    ) {
      lastAutoSubmittedDeeplink = deeplink;
      setShouldAutoSubmit(true);
    }
  }, [location.search, location.hash, setFieldValue]);

  // Auto-submit after form values are populated from query params
  useEffect(() => {
    if (
      shouldAutoSubmit &&
      !hasAutoSubmitted.current &&
      formik.values.email &&
      formik.values.password
    ) {
      setShouldAutoSubmit(false);
      hasAutoSubmitted.current = true;
      handleSubmit();
    }
  }, [shouldAutoSubmit, formik.values.email, formik.values.password, handleSubmit]);

  // After a failed submit, move focus to the first invalid field. Formik bumps
  // submitCount BEFORE validation finishes, so remember the attempt and act once
  // the submit has settled (isSubmitting false) with the resulting errors.
  // Input is not a forwardRef, so look the element up by id.
  const { submitCount, isSubmitting, errors } = formik;
  const focusAfterSubmit = useRef(false);
  useEffect(() => {
    if (submitCount > 0) focusAfterSubmit.current = true;
  }, [submitCount]);
  useEffect(() => {
    if (!focusAfterSubmit.current || isSubmitting) return;
    focusAfterSubmit.current = false;
    const firstInvalid = errors.email
      ? 'email'
      : errors.password
        ? 'password'
        : null;
    if (firstInvalid) document.getElementById(firstInvalid)?.focus();
  }, [submitCount, isSubmitting, errors.email, errors.password]);

  return (
    <div className="flex justify-center items-center h-screen">
      <Card className="w-full max-w-md mx-auto">
        <div className="flex flex-col gap-4">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            {tLogin.login}
          </h1>
          <p className="text-gray-500 dark:text-gray-400">
            {tLogin.subtitle}
          </p>
        </div>
        {showAlertForceChangePassword && (
          <Alert className="mt-4" variant="info">
            <p>{tLogin.changePasswordToContinue}</p>
          </Alert>
        )}
        <form
          noValidate
          onSubmit={formik.handleSubmit}
          autoComplete="on"
          className="flex flex-col gap-4 mt-8">
          <FormGroup label={tLogin.email} htmlFor="email" error={emailError}>
            <Input
              id="email"
              required
              aria-invalid={Boolean(emailError)}
              aria-describedby={emailError ? 'email-error' : undefined}
              type="email"
              name="email"
              autoComplete="email"
              value={formik.values.email}
              placeholder={tLogin.enterEmail}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={Boolean(formik.touched.email && formik.errors.email)}
              data-testid="login-email-input"
            />
          </FormGroup>
          <FormGroup
            label={tLogin.password}
            htmlFor="password"
            error={passwordError}>
            <Input
              id="password"
              required
              aria-invalid={Boolean(passwordError)}
              aria-describedby={passwordError ? 'password-error' : undefined}
              type="password"
              name="password"
              autoComplete="current-password"
              value={formik.values.password}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={Boolean(formik.touched.password && formik.errors.password)}
              placeholder={tLogin.enterPassword}
              data-testid="login-password-input"
            />
          </FormGroup>

          <Button type="submit" variant="primary" fullWidth data-testid="login-submit-button">
            {tLogin.login}
          </Button>
        </form>
        <div className="flex flex-col gap-3 mt-4">
          <div className="flex justify-center items-center">
            <button
              type="button"
              onClick={onForgotPassword}
              className={`text-sm text-gray-500 dark:text-gray-400 cursor-pointer hover:text-gray-700 dark:hover:text-gray-300 ${LINK_FOCUS_CLASSES}`}
              data-testid="login-forgot-password-link">
              {tLogin.forgotPassword}
            </button>
          </div>
          <div className="flex justify-center items-center">
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {tLogin.noAccount}{' '}
              <Link
                to="/register"
                data-testid="login-register-link"
                className={`text-blue-600 dark:text-blue-400 hover:underline ${LINK_FOCUS_CLASSES}`}>
                {tLogin.signUp}
              </Link>
            </p>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default LoginForm;
